import { z } from 'zod';
import { ShortIdSchema, TimestampSchema } from './primitives.js';

/**
 * Customer segment — a reusable, operator-authored audience filter.
 *
 * Lives at `tenants/{tid}/customerSegments/{id}`. A segment is a saved set of
 * AND-combined conditions evaluated against the tenant customer index
 * (`tenants/{tid}/customers/{uid}` — `TenantCustomerIndexSchema`). Operators
 * target marketing broadcasts (and, later, bulk voucher issuance) at a segment
 * instead of re-picking prebuilt audiences every time.
 *
 * Resolution is server-side: `runCampaignNow` (marketing dispatcher) loads the
 * segment, scans the customer index (bounded, truncate-with-report), and keeps
 * every customer that `matchesSegment(...)` returns true for. The evaluation
 * itself is the pure `matchesSegment` helper in
 * `packages/shared/src/marketing/segment-match.ts` — one source of truth shared
 * by the server dispatcher and the dashboard's live audience estimate.
 *
 * `name` is a PLAIN operator label (like a voucher template's internal name),
 * not an i18n map — it's never shown to customers, only to the operator who
 * authored it.
 */

/**
 * The AND-combined conditions of a segment. EVERY field is optional; a segment
 * with no conditions (`{}`) matches every customer in the index. A present
 * condition narrows the audience; an absent one imposes no constraint.
 */
export const CustomerSegmentCriteriaSchema = z
  .object({
    tierIdIn: z
      .array(z.string())
      .optional()
      .describe("Customer's current tier id must be one of these (empty/absent = any tier)"),
    tagIdsAny: z
      .array(z.string())
      .optional()
      .describe('Customer must carry AT LEAST ONE of these operator tag ids (empty/absent = any)'),
    minSpendSen: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe('Lifetime spend (minor units) must be >= this'),
    maxSpendSen: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe('Lifetime spend (minor units) must be <= this'),
    minVisits: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe('Lifetime paid-visit count must be >= this'),
    maxVisits: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe('Lifetime paid-visit count must be <= this'),
    inactiveDays: z
      .number()
      .int()
      .positive()
      .optional()
      .describe('Last visit must be STRICTLY older than this many days ago (win-back audiences)'),
    newWithinDays: z
      .number()
      .int()
      .positive()
      .optional()
      .describe(
        'First visit must be within the last this-many days (welcome/onboarding audiences)',
      ),
  })
  .describe('AND-combined segment conditions; empty object = all customers');
export type CustomerSegmentCriteria = z.infer<typeof CustomerSegmentCriteriaSchema>;

export const CustomerSegmentSchema = z.object({
  id: ShortIdSchema.describe('Short id; doc id at tenants/{tid}/customerSegments/{id}'),
  name: z
    .string()
    .min(1)
    .max(80)
    .describe('Plain operator label (not i18n, not customer-visible), e.g. "Lapsed Gold VIPs"'),
  criteria: CustomerSegmentCriteriaSchema.default({}).describe(
    'The AND-combined audience filter; empty = all customers',
  ),
  active: z
    .boolean()
    .default(true)
    .describe('Inactive segments are ignored by the dispatcher (resolve to an empty audience)'),
  createdAt: TimestampSchema.describe('When the segment was first created'),
  updatedAt: TimestampSchema.describe('When the segment was last edited'),
});
export type CustomerSegment = z.infer<typeof CustomerSegmentSchema>;
